import React, { useContext, useState, useEffect } from 'react';
import { ArrowRight, Play, Star, X } from 'lucide-react';
import { LanguageContext } from '../context/LanguageContext';
import { Container, Eyebrow } from './ui/Section';
import { Button } from './ui/Button';
import { ActivationsCounter } from './ui/FooterExtras';

const copy = {
  en: {
    badge: 'New',
    rebrand: 'WashPayApp is now WashWorth. Same service, new name.',
    preOrder: 'Pre-order now',
    watchDemo: 'Watch demo',
    closeAlert: 'Close announcement',
    closeVideo: 'Close video',
  },
  es: {
    badge: 'Nuevo',
    rebrand: 'WashPayApp ahora es WashWorth. El mismo servicio, con nuevo nombre.',
    preOrder: 'Reservar ahora',
    watchDemo: 'Ver demo',
    closeAlert: 'Cerrar aviso',
    closeVideo: 'Cerrar video',
  },
};

const HeroBanner = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [showRebrandAlert, setShowRebrandAlert] = useState(true);
  const { heroTitle, heroSmart, heroDescription, ratingText, tecnologia, heroProof = [], language = 'en' } =
    useContext(LanguageContext);
  const t = copy[language] || copy.en;
  const preOrderPath = language === 'es' ? '/es/pre-order' : '/pre-order';
  // WashKiosk demo, one recording per language (vertical, H.264). File names are case-sensitive on Netlify.
  const demoVideo = language === 'es' ? '/washkiosk-es.MP4' : '/washkiosk-en.mp4';

  useEffect(() => {
    try {
      if (localStorage.getItem('rebrandAlertClosed') === 'true') setShowRebrandAlert(false);
    } catch (e) {}
  }, []);

  const handleCloseRebrandAlert = () => {
    setShowRebrandAlert(false);
    try {
      localStorage.setItem('rebrandAlertClosed', 'true');
    } catch (e) {}
  };

  // Translation strings end with " —" as a lead-in to heroSmart; the layout splits them onto two lines instead.
  const title = (heroTitle || '').replace(/\s*[—-]\s*$/, '');

  return (
    <>
      {showRebrandAlert && (
        <div className="bg-brand-navy text-white">
          <Container className="flex items-center justify-between gap-4 py-2.5">
            <p className="mb-0 text-sm text-white/80">
              <span className="mr-2 inline-block rounded-full bg-brand-mint px-2 py-0.5 text-xs font-semibold text-brand-ink">
                {t.badge}
              </span>
              {t.rebrand}
            </p>
            <button
              type="button"
              onClick={handleCloseRebrandAlert}
              className="rounded-full p-1 text-white/60 transition-colors hover:text-white"
              aria-label={t.closeAlert}
            >
              <X className="h-4 w-4" />
            </button>
          </Container>
        </div>
      )}

      <section className="bg-white">
        <Container className="grid items-center gap-12 py-16 md:py-24 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6">
            {tecnologia && <Eyebrow>{tecnologia}</Eyebrow>}

            <h1 className="mb-0 font-display text-4xl font-extrabold leading-[1.04] tracking-tight text-brand-navy sm:text-5xl xl:text-6xl">
              {title}
              <span className="mt-2 block text-brand-teal">{heroSmart}</span>
            </h1>

            <p className="mb-0 mt-6 max-w-xl text-lg leading-relaxed text-slate-600">{heroDescription}</p>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Button to={preOrderPath} size="lg">
                {t.preOrder}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button variant="secondary" size="lg" onClick={() => setShowVideo(true)}>
                <Play className="h-4 w-4" />
                {t.watchDemo}
              </Button>
            </div>

            {heroProof.length > 0 && (
              <dl className="mt-10 grid grid-cols-3 gap-4 border-t border-brand-line pt-6">
                {heroProof.map((item) => (
                  <div key={item.label}>
                    <dt className="font-display text-2xl font-extrabold tracking-tight text-brand-navy sm:text-3xl">
                      {item.key === 'activations' ? <ActivationsCounter language={language} /> : item.value}
                    </dt>
                    <dd className="mb-0 ml-0 mt-1 text-xs leading-snug text-slate-500 sm:text-sm">{item.label}</dd>
                  </div>
                ))}
              </dl>
            )}

            {ratingText && (
              <div className="mt-6 flex items-center gap-3">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="h-4 w-4 fill-brand-teal text-brand-teal" />
                  ))}
                </div>
                <span className="text-sm font-medium text-slate-600">{ratingText}</span>
              </div>
            )}
          </div>

          <div className="lg:col-span-6">
            <div className="relative overflow-hidden rounded-[2rem] bg-brand-foam">
              <img
                src="/heroTimmy.webp"
                width="800"
                height="800"
                alt="WashWorth app on a phone, adding credit to activate a laundromat machine"
                fetchpriority="high"
                className="relative mx-auto aspect-square w-full max-w-xl object-contain p-6 mix-blend-multiply sm:p-10"
              />
            </div>
          </div>
        </Container>
      </section>

      {showVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/80 p-4"
          onClick={() => setShowVideo(false)}
        >
          <div
            className="relative overflow-hidden rounded-2xl bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowVideo(false)}
              className="absolute right-3 top-3 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
              aria-label={t.closeVideo}
            >
              <X className="h-5 w-5" />
            </button>
            <video
              key={demoVideo}
              className="block max-h-[85vh] w-auto max-w-[90vw]"
              controls
              autoPlay
              muted
              playsInline
            >
              <source src={demoVideo} type="video/mp4" />
            </video>
          </div>
        </div>
      )}
    </>
  );
};

export default HeroBanner;
